import { GuitarLib, NoteLib, prettifyNote, ScaleLib } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import { getDesignTokens } from '@tunasong/ui-lib'
import classNames from 'classnames'
import type { FC, MouseEvent } from 'react'
import React, { useCallback, useEffect, useState } from 'react'
import invariant from 'tiny-invariant'
import { getGuitarColors } from './guitar-colors.js'
export interface FretboardProps {
  className?: string

  /** Specify start and end frets. If not specified, fingerings will be used */
  start?: number
  end?: number

  watermark?: boolean

  /** Fret fingerings */
  fingerings?: GuitarLib.FingeringWithMetadata[]

  /** Type of content for the labels */
  labels?: 'note' | 'finger' | 'degree' | 'none'

  // Scale
  scale?: Scale | null

  // Color-code the fingerings when in scale
  colors?: boolean

  /** Callbacks */
  onSelectFingering?(fingering: GuitarLib.FingeringWithMetadata): void
  onHoverFingering?(ev: MouseEvent, fingering: GuitarLib.FingeringWithMetadata): void
}

interface Dimensions {
  canvas: HTMLCanvasElement
  pixelRatio: number
  height: number
  width: number
  // The padding
  padding: number
  fingerRadius: number
  fretSpacing: number
  nutWidth: number
  stringSpacing: number
  inlayRadius: number
}

const canvasStyle: React.CSSProperties = {
  flex: 1,
  width: '100%',
  height: 150,
  cursor: 'pointer',
}

const getLabelText = ({
  fingering,
  scale,
  note,
  labels,
}: {
  fingering: GuitarLib.FingeringWithMetadata
  note: NoteName
  scale?: Scale | null
  labels: 'note' | 'degree' | 'finger' | 'none'
}) => {
  const prettyNoteName = prettifyNote(scale ? ScaleLib.getScaleNoteName(scale, note) : note)
  const prettyScaleDegree = fingering.scaleDegree ? NoteLib.prettifyNoteDegree(fingering.scaleDegree) : null

  if (labels === 'none') {
    return ''
  }

  const text =
    labels === 'note'
      ? prettyNoteName
      : labels === 'finger'
        ? `${fingering.finger ?? ''}`
        : `${prettyScaleDegree ?? ''}`
  return text
}

export const Fretboard: FC<FretboardProps> = props => {
  const {
    className,
    fingerings = [],
    scale,
    onSelectFingering,
    onHoverFingering,
    watermark = true,
    labels = 'note',
    start = 1,
    end = 12,
    colors: scaleColors = true,
  } = props
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const [startFret] = useState(start)
  const [endFret] = useState(end)

  /** Constants */

  const lineWidth = 4
  const fretLineWidth = 4

  const numStrings = 6
  const nutWidth = 15

  const stringWidth = 2
  const stringPadding = 8

  const numFrets = endFret - startFret
  /** End constants */

  // Set the canvas size based on client size and device pixel ratio
  const setCanvasSize = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    const pixelRatio = window.devicePixelRatio || 1
    canvas.height = canvas.clientHeight * pixelRatio
    canvas.width = canvas.clientWidth * pixelRatio
  }, [])

  const getDimensions = useCallback((): Dimensions | null => {
    const canvas = canvasRef.current
    if (!canvas) {
      return null
    }
    const pixelRatio = window.devicePixelRatio || 1
    // canvas.height and canvas.width are now set elsewhere
    const width = canvas.offsetWidth
    const height = canvas.offsetHeight
    const padding = 4

    return {
      canvas,
      pixelRatio,
      width,
      height,
      fingerRadius: 10,
      padding,
      nutWidth,
      fretSpacing: (width - nutWidth - 2 * padding) / numFrets,
      inlayRadius: 8,
      stringSpacing: (height - 2 * stringPadding - 2 * padding) / (numStrings - 1),
    }
  }, [numFrets, stringPadding])

  const draw = useCallback(() => {
    setCanvasSize()
    const dimensions = getDimensions()
    if (!dimensions) {
      return
    }
    const {
      canvas,
      pixelRatio,
      width,
      height,
      inlayRadius,
      fretSpacing,
      stringSpacing,
      fingerRadius,
      padding,
      nutWidth,
    } = dimensions

    /**
     * Read the colours where the canvas is, not on the document root: a page that pins a theme on a subtree
     * (the public games page is dark for every visitor) redefines the tokens there.
     */
    const {
      outlineStyle,
      watermarkStyle,
      nutStyle,
      fretStyle,
      stringStyle,
      inlayStyle,
      labelText,
      fingeringStyle,
      fingeringStyleInScale,
      fingeringStyleOutOfScale,
      fingeringStyleRoot,
    } = getGuitarColors(getDesignTokens(canvas))

    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }

    if (!ctx) {
      return
    }
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    const fontSize = 12
    ctx.font = `${fontSize}px`

    /**
     * Get the starting X for a fret area. There is an area of size fretSpacing **before** the fret.
     */
    const getFretX = (fret: number) => padding + nutWidth + (fret - startFret + 1) * fretSpacing

    const drawMute = (str: number) => {
      ctx.beginPath()
      const x = padding + 6
      const y = padding + stringPadding + stringSpacing * (str - 1)
      const label = `X`
      const size = 20
      ctx.font = `bold ${size}px courier`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(label, x, y)
    }

    /** Use e.g., 2.5 to draw between strings */
    const drawDot = (fret: number, str: number, radius: number, style: string) => {
      ctx.beginPath()
      /** The dot is halfway in the area before the fret */
      const x = getFretX(fret) - fretSpacing / 2
      const y = padding + stringPadding + stringSpacing * (str - 1)
      ctx.fillStyle = style
      ctx.moveTo(x, y)
      const circle = new Path2D()
      circle.arc(x, y, radius, 0, Math.PI * 2)
      ctx.fill(circle)
      return circle
    }

    /** Draw a dot with text inside */
    const drawDotWithText = (fret: number, str: number, radius: number, style: string, text: string) => {
      ctx.beginPath()
      /** The dot is halfway in the area before the fret */
      const x = getFretX(fret) - fretSpacing / 2
      const y = padding + stringPadding + stringSpacing * (str - 1)

      // Draw the circle
      const circle = new Path2D()
      circle.arc(x, y, radius, 0, Math.PI * 2)
      ctx.fillStyle = style
      ctx.moveTo(x, y)
      ctx.fill(circle)

      // Draw the text
      // Adjust fontSize so text fits inside the circle
      let fontSize = Math.round(Math.max(radius * 1.8, 10))
      ctx.font = ` ${fontSize}px courier`
      // Shrink font size if text is too wide for the circle
      while (ctx.measureText(text).width > radius * 1.8 && fontSize > 8) {
        fontSize -= 1
        ctx.font = ` ${fontSize}px courier`
      }
      ctx.fillStyle = labelText
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(text, x, y)

      return circle
    }

    /** Draw fretboard box, including nut */
    ctx.beginPath()
    ctx.moveTo(padding, 0)
    ctx.strokeStyle = outlineStyle
    ctx.lineWidth = lineWidth
    ctx.strokeRect(padding, 0, width, height)
    ctx.stroke()

    /** Nut */
    ctx.beginPath()
    ctx.lineWidth = nutWidth * pixelRatio
    ctx.strokeStyle = nutStyle
    ctx.moveTo(padding, 0)
    ctx.lineTo(padding, height)
    ctx.stroke()

    /** Set the line width for the frets */
    ctx.beginPath()
    ctx.lineWidth = fretLineWidth
    ctx.strokeStyle = fretStyle

    for (let fret = startFret; fret <= endFret; fret++) {
      /**
       * Fret position is nutWidth + fretSpacing times the frets,
       * We need to add an initial spacing for the first displayed fret
       */
      const x = getFretX(fret)
      const y = padding + height

      /** Watermark */
      if (watermark) {
        const label = `${fret}`
        ctx.fillStyle = watermarkStyle
        const labelSize = ctx.measureText(label)
        ctx.fillText(label, x - fretSpacing / 2 - labelSize.width / 2, y / 2 + fontSize / 3 - padding / 2)
      }

      ctx.beginPath()
      ctx.strokeStyle = fretStyle
      ctx.moveTo(x, 0)
      ctx.lineTo(x, y)
      ctx.stroke()
    }

    /** Strings */
    ctx.beginPath()
    ctx.lineWidth = stringWidth
    ctx.strokeStyle = stringStyle
    for (let str = 0; str < numStrings; str++) {
      const y = padding + stringPadding + stringSpacing * str
      ctx.moveTo(padding, y)
      ctx.lineTo(width, y)
      ctx.stroke()
    }

    /** Inlays */
    const inlays = [3, 5, 7, 10, 15, 17, 19, 22]
    ctx.beginPath()
    ctx.lineWidth = fretLineWidth
    for (const fret of inlays) {
      drawDot(fret, 3.5, inlayRadius, inlayStyle)
    }
    const doubleInlays = [12, 24]
    for (const fret of doubleInlays) {
      drawDot(fret, 2.5, inlayRadius, inlayStyle)
      drawDot(fret, 4.5, inlayRadius, inlayStyle)
    }

    /** Fingerings */

    for (const fingering of fingerings) {
      const { fret, str, inScale, note } = fingering

      const isRootNote = note === scale?.root

      if (fret === GuitarLib.MUTED_STRING) {
        drawMute(str)
      } else if (fret === GuitarLib.OPEN_STRING) {
        /** No-op */
      } else {
        const color = scaleColors
          ? isRootNote
            ? fingeringStyleRoot
            : inScale === true
              ? fingeringStyleInScale
              : inScale === false
                ? fingeringStyleOutOfScale
                : fingeringStyle
          : fingeringStyle // Default color

        const note = GuitarLib.getNoteFromFingering(fingering)
        if (note) {
          const text = getLabelText({
            fingering,
            scale,
            note,
            labels,
          })
          drawDotWithText(fret, str, fingerRadius, color, text)
        } else {
          drawDot(fret, str, fingerRadius, color)
        }
      }
    }
  }, [endFret, fingerings, getDimensions, labels, scale, scaleColors, setCanvasSize, startFret, watermark])

  const getEventPosition = useCallback((ev: React.MouseEvent) => {
    const canvas = ev.currentTarget as HTMLCanvasElement
    const rect = canvas.getBoundingClientRect()
    const x = ev.pageX - rect.left
    const y = ev.pageY - rect.top
    return { x, y }
  }, [])

  const getStringFret = useCallback(
    ({ x, y }: { x: number; y: number }) => {
      const dimensions = getDimensions()
      if (!dimensions) {
        return null
      }
      /** @todo this will not work unless displaying the whole fingerboard */
      const fret = x <= nutWidth ? -1 : Math.floor((x - nutWidth) / dimensions.fretSpacing) + startFret

      const str = Math.round((y + stringPadding) / dimensions.stringSpacing + 0.5) as GuitarLib.StringNumber

      return { fret, str }
    },
    [getDimensions, startFret]
  )

  const handleClick = useCallback(
    (ev: React.MouseEvent) => {
      if (!onSelectFingering) {
        return
      }
      const { x, y } = getEventPosition(ev)
      const stringFret = getStringFret({ x, y })
      if (!stringFret) {
        return
      }
      const { fret, str } = stringFret

      if (onSelectFingering) {
        const fingering = GuitarLib.getFingeringWithMetadata([{ fret, str }], scale)
        onSelectFingering(fingering[0])
      }
    },
    [getEventPosition, getStringFret, onSelectFingering, scale]
  )
  const handleMouseMove = (event: MouseEvent) => {
    invariant(onHoverFingering, 'onHoverFingering is not defined')
    const { x, y } = getEventPosition(event)
    const stringFret = getStringFret({ x, y })
    if (!stringFret) {
      return
    }
    const fingering = GuitarLib.getFingeringWithMetadata([stringFret], scale)
    onHoverFingering(event, fingering[0])
  }

  useEffect(() => {
    /** Redraw on resize */
    const redraw = () => {
      draw()
    }
    window.addEventListener('resize', redraw)

    /** Unsubscribe on unmount */
    return () => {
      window.removeEventListener('resize', redraw)
    }
  }, [draw, fingerings])

  // Draw on every render
  useEffect(() => {
    draw()
  })

  return (
    <canvas
      style={canvasStyle}
      className={classNames(className)}
      onMouseMove={onHoverFingering ? handleMouseMove : undefined}
      onClick={handleClick}
      ref={canvasRef}
    />
  )
}

export default Fretboard
